import { createColumnHelper, type Row } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { fetchInvoiceDocument, fetchInvoiceDocumentAttachments } from '../../../../api/api'
import { fetchInvoiceDetailsReport } from '../../../../api/newApi'
import { useChosenCompany, useUser } from '../../../../context/UserProvider'
import type { InvoiceDetailLine } from '../../../../types'
import LinkButton from '../../../../components/LinkButton'
import Pdf from '../../../../icons/Pdf'
import GenericTable from '../../../cars/components/V3/_shared/GenericTable/GenericTable'
import { TableMobileCardContent } from '../../../cars/components/V3/_shared/GenericTable/TableMobileCardContent'
import { formatCurrency } from '../utils/formatCurrency'
import { ExcelExportButton } from '../components/ExcelExporter'
import { ReportTextCell } from '../components/ReportTextCell'

const DESCRIPTION_MAX_LENGTH = 50

const InvoiceReportDetails = () => {
  const { consolidatedInvoiceNo, invoiceNo } = useParams()
  const companyId = useChosenCompany()?.id
  const contactId = useUser()?.user?.id

  const { data: response, status } = useQuery(
    ['invoiceDetailsReport', companyId, contactId, invoiceNo],
    () => fetchInvoiceDetailsReport({ companyId, contactId, invoiceNo, fetchAll: true }),
    {
      enabled: !!companyId && !!contactId && !!invoiceNo,
    },
  )

  const items = (response && 'items' in response ? response.items : []) as InvoiceDetailLine[]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-row justify-between items-center gap-2">
          <span className="text-xl font-medium text-primaryText">Fakturadetaljer</span>
          <ExcelExportButton
            className="h-fit"
            data={items}
            config={{
              filename: `Fakturadetaljer_${invoiceNo}.xlsx`,
              sheetName: 'Fakturadetaljer',
              columns: invoiceDetailsExcelColumns,
            }}
          />
        </div>
        <span className="text-sm text-secondaryText">
          Samlefaktura {consolidatedInvoiceNo} / Faktura {invoiceNo}
        </span>
      </div>

      <GenericTable
        className="max-h-[65vh]"
        data={items}
        isLoading={status === 'loading'}
        columns={columns}
        columnWidths={{
          description: { width: 420, minWidth: 280, maxWidth: 560 },
        }}
        mobileCardContent={(row) => <MobileCardContent row={row as Row<InvoiceDetailLine>} />}
      />
    </div>
  )
}

export default InvoiceReportDetails

function MobileCardContent({ row }: { row: Row<InvoiceDetailLine> }) {
  const header = `Dokument - ${row.original.documentNo}`
  const data = columns.map((column) => {
    const id = column.id as keyof InvoiceDetailLine
    const value =
      id === 'amountInclVAT' ? (
        formatCurrency(row.original[id] as number)
      ) : id === 'description' ? (
        <InvoiceDocumentLink key={id} line={row.original}>
          {truncateText(String(row.original[id] || ''), DESCRIPTION_MAX_LENGTH)}
        </InvoiceDocumentLink>
      ) : (
        row.original[id]
      )

    return {
      label: String(column.header),
      value: value || '',
    }
  })

  return <TableMobileCardContent header={header} data={data} />
}

function InvoiceDocumentLink({ line, children }: { line: InvoiceDetailLine; children: ReactNode }) {
  const sourceID = line.docuBizzSourceID
  const sourceType = Number(line.sourceType)
  const filename = `${line.documentNo}-${line.lineNo}`

  if (sourceType === 0 && sourceID) {
    return (
      <LinkButton
        onClick={() => fetchInvoiceDocument(sourceID, filename)}
        className="!ml-0 !text-sm inline-flex items-center gap-1 min-w-0 max-w-full"
      >
        <Pdf />
        {children}
      </LinkButton>
    )
  }

  if (sourceType === 2 && sourceID) {
    return (
      <LinkButton
        onClick={() => fetchInvoiceDocumentAttachments(sourceID, filename)}
        className="!ml-0 !text-sm inline-flex items-center gap-1 min-w-0 max-w-full"
      >
        <Pdf />
        {children}
      </LinkButton>
    )
  }

  return <>{children}</>
}

const columnHelper = createColumnHelper<InvoiceDetailLine>()

const invoiceDetailsExcelColumns = [
  { key: 'documentNo', header: 'DocumentNr', width: 15 },
  { key: 'description', header: 'Detaljer', width: 60 },
  { key: 'lineNo', header: 'LinjeNr', width: 12 },
  { key: 'postingGroup', header: 'Bokføringsgruppe', width: 20 },
  { key: 'amountInclVAT', header: 'Beløp', width: 18 },
]

const columns = [
  columnHelper.accessor('documentNo', {
    id: 'documentNo',
    header: 'Dokumentnr',
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('description', {
    id: 'description',
    header: 'Beskrivelse',
    enableSorting: false,
    cell: ({ row, getValue }) => (
      <InvoiceDocumentLink line={row.original}>
        <ReportTextCell value={String(getValue() || '')} />
      </InvoiceDocumentLink>
    ),
  }),
  columnHelper.accessor('postingGroup', {
    id: 'postingGroup',
    header: 'Bokføringsgruppe',
    enableSorting: false,
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('lineNo', {
    id: 'lineNo',
    header: 'Linjenr',
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('amountInclVAT', {
    id: 'amountInclVAT',
    header: 'Beløp inkl. mva',
    meta: {
      alignmentType: 'right',
      mobileFooterLabel: 'Sum beløp inkl. mva',
    },
    cell: (row) => formatCurrency(row.getValue()) || '',
    footer: ({ table }) => {
      const total = table
        .getFilteredRowModel()
        .rows.reduce((sum, row) => sum + ((row.getValue('amountInclVAT') as number) || 0), 0)
      return formatCurrency(total)
    },
  }),
]

function truncateText(value: string, maxLength: number) {
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength).trimEnd()}...`
}
